<script>
export default {
  inheritAttrs: false,
};
</script>

<script setup>
import { tv } from 'tailwind-variants';
import { computed, nextTick, onMounted, ref, useSlots } from 'vue';
import { CsFormError, CsFormHelpText, CsFormLabel } from '../form';
import { CsIcon } from '../icon';
import { generateId } from '../shareds';

/**
 * @typedef {import('../icon/icon-types').IconName} IconName
 */

/**
 * @typedef {'bottom-start'|'top'|'right'|'bottom'|'left'|'top-start'|'top-end'|'right-start'|'right-end'|'bottom-end'|'left-start'|'left-end'} TooltipPlacement
/**
 * @typedef {object} InputProps
 * @property {string|number} [value=''] - The value of the input
 * @property {string} [placeholder=''] - Placeholder text for the input
 * @property {string} [label=''] - Label text for the input
 * @property {string} [labelTooltip=''] - Tooltip text for the label
 * @property {TooltipPlacement} [labelTooltipPlacement='right'] - Placement of the label tooltip
 * @property {string} [helpText=''] - Help text displayed below the input
 * @property {string} [name=''] - Name attribute for the input
 * @property {boolean} [isClearable=true] - Whether the input can be cleared
 * @property {string} [id=''] - ID attribute for the input
 * @property {boolean} [isReadonly=false] - Whether the input is read-only
 * @property {boolean} [isDisabled=false] - Whether the input is disabled
 * @property {boolean} [isRequired=false] - Whether the input is required
 * @property {boolean} [isError=false] - Whether the input is in error state
 * @property {string} [error=''] - Error message for the input
 * @property {string} [inputClassName=''] - Class name for the input
 * @property {IconName} [prefixIcon=''] - Name of the icon to display as prefix
 * @property {IconName} [suffixIcon=''] - Name of the icon to display as suffix
 * @property {'default'|'sm'} [size='default'] - Size of the input
 */

/**
 * @type {InputProps}
 */
const props = defineProps({
  value: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  labelTooltip: {
    type: String,
    default: '',
  },
  labelTooltipPlacement: {
    type: String,
    default: 'right',
  },
  helpText: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  isClearable: {
    type: Boolean,
    default: true,
  },
  id: {
    type: String,
    default: '',
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  inputClassName: {
    type: String,
    default: '',
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'default',
  },
});

const emit = defineEmits([
  'input',
  'change',
  'clear',
  'focus',
  'blur',
  'keydown',
]);

const input = tv({
  slots: {
    root: 'group relative flex flex-col',
    inputWrapper: 'group relative flex items-center',
    input: [
      'w-full text-brand-text placeholder:text-brand-gray-13 rounded-lg border',
      'focus:outline-none',
    ],
  },
  variants: {
    size: {
      default: {
        input:
          'py-1.5 pl-[calc(12px+var(--prefix-width))] pr-[calc(12px+var(--suffix-width))] text-sm',
      },
      sm: {
        input:
          'py-1 pl-[calc(12px+var(--prefix-width))] pr-[calc(12px+var(--suffix-width))] text-sm',
      },
    },
    variant: {
      default: {
        input: [
          'bg-brand-gray-1 dark:bg-brand-gray-16',
          'group-hover:bg-brand-gray-3 group-hover:dark:bg-brand-gray-16',
          'border-brand-gray-12 dark:border-brand-gray-13',
          'group-hover:border-brand-gray-13 dark:group-hover:border-brand-gray-14',
          'focus:border-brand-gray-16 dark:focus:border-brand-gray-16',
        ],
      },
      readonly: {
        input: [
          'bg-brand-gray-1 dark:bg-brand-gray-16',
          'group-hover:bg-brand-gray-3 group-hover:dark:bg-brand-gray-16',
          'border-brand-gray-12 dark:border-brand-gray-13',
          'group-hover:border-brand-gray-13 dark:group-hover:border-brand-gray-14',
          'focus:border-brand-gray-16 dark:focus:border-brand-gray-16',
          'read-only:bg-brand-gray-1 dark:read-only:bg-brand-gray-16',
          'read-only:border-brand-gray-12 dark:read-only:border-brand-gray-13',
          'read-only:group-hover:bg-brand-gray-3 group-hover:dark:read-only:bg-brand-gray-16',
          'read-only:group-hover:border-brand-gray-13 dark:read-only:group-hover:border-brand-gray-14',
          'read-only:focus:border-brand-gray-16 dark:read-only:focus:border-brand-gray-16',
          'read-only:cursor-pointer',
        ],
      },
      disabled: {
        input: [
          'cursor-not-allowed',
          'disabled:bg-brand-black-alpha-5 dark:disabled:bg-brand-gray-14',
        ],
      },
      error: {
        input: [
          'bg-brand-red-4 dark:bg-brand-red-16',
          'group-hover:bg-brand-red-4 group-hover:dark:bg-brand-red-16',
          'border-brand-red-12 dark:border-brand-red-13',
          'group-hover:border-brand-red-13 dark:group-hover:border-brand-red-14',
          'focus:border-brand-red-16 dark:focus:border-brand-red-16',
          'focus:bg-brand-red-4 dark:focus:bg-brand-red-16',
        ],
      },
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

function getVariant() {
  if (props.isDisabled) return 'disabled';
  if (props.isReadonly) return 'readonly';
  if (props.isError || props.error?.length) return 'error';

  return 'default';
}

const inputClasses = computed(() => {
  return input({
    size: props.size,
    variant: getVariant(),
  });
});

const inputRef = ref(null);
const isFocused = ref(false);

const isRenderClearButton = computed(() => props.isClearable && props.value);

const prefixWidth = ref(0);
const suffixWidth = ref(0);

const prefixNode = ref(null);
const suffixNode = ref(null);

function handleUpdateModelValue(event) {
  emit('input', event.target.value);
}

function handleChange(event) {
  emit('change', event.target.value);
}

function handleClearInput() {
  emit('input', '');
  emit('change', '');
  emit('clear');
}

function onFocus() {
  isFocused.value = true;

  emit('focus');
}

function onBlur() {
  isFocused.value = false;

  emit('blur');
}

function onKeydown(event) {
  emit('keydown', event);
}

function recalculatePrefixAndSuffixWidth() {
  prefixWidth.value =
    prefixNode.value && prefixNode.value.offsetWidth != null
      ? prefixNode.value.offsetWidth + 2
      : 0;
  suffixWidth.value =
    suffixNode.value && suffixNode.value.offsetWidth != null
      ? suffixNode.value.offsetWidth + 2
      : 0;
}

onMounted(() => {
  nextTick(() => {
    recalculatePrefixAndSuffixWidth();
  });
});

const slots = useSlots();
function hasSlot(name) {
  return !!slots[name];
}

const inputId = computed(() => props.id || generateId(10));
const inputName = computed(() => props.name || generateId(10));

defineExpose({
  input: inputRef,
  focus: () => inputRef.value.focus(),
  blur: () => inputRef.value.blur(),
  recalculatePrefixAndSuffixWidth: () => recalculatePrefixAndSuffixWidth(),
});
</script>

<template>
  <div
    data-component="CsInput"
    :class="inputClasses.root()"
    :data-required="props.isRequired || undefined"
    :data-variant="getVariant()"
  >
    <div v-if="props.label" class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <CsFormLabel :for="inputId" :is-required="props.isRequired">
          {{ props.label }}
        </CsFormLabel>
        <CsIcon
          v-if="props.labelTooltip"
          v-cs-tooltip="{
            content: props.labelTooltip,
            placement: props.labelTooltipPlacement,
          }"
          name="info"
          class="text-brand-gray-12 dark:text-brand-gray-11"
        />
      </div>

      <slot name="label-actions" />
    </div>

    <div
      :class="inputClasses.inputWrapper()"
      :style="{
        '--prefix-width': `${prefixWidth}px`,
        '--suffix-width': `${suffixWidth}px`,
      }"
    >
      <div
        v-if="$slots.prefix || prefixIcon"
        ref="prefixNode"
        class="absolute left-[12px]"
      >
        <CsIcon
          v-if="prefixIcon"
          :name="prefixIcon"
          variant-color="secondary"
        />
        <slot v-else name="prefix" />
      </div>
      <input
        :id="inputId"
        ref="inputRef"
        :class="inputClasses.input({ className: props.inputClassName })"
        :name="inputName"
        :required="props.isRequired"
        :readonly="props.isReadonly"
        :disabled="props.isDisabled"
        :placeholder="props.placeholder"
        v-bind="$attrs"
        :value="props.value"
        @input="handleUpdateModelValue"
        @change="handleChange"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />

      <div
        ref="suffixNode"
        data-slot="suffix"
        class="absolute right-[8px] pointer-events-none flex"
      >
        <div v-if="isRenderClearButton" class="flex items-center gap-2">
          <button
            v-cs-tooltip="'Clear'"
            type="button"
            tabindex="-1"
            class="transition-opacity duration-100 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto cursor-pointer"
            @click="handleClearInput"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M13.03 6.97a.75.75 0 0 1 0 1.06L11.06 10l1.97 1.97a.75.75 0 1 1-1.06 1.06L10 11.06l-1.97 1.97a.75.75 0 0 1-1.06-1.06L8.94 10 6.97 8.03a.75.75 0 0 1 1.06-1.06L10 8.94l1.97-1.97a.75.75 0 0 1 1.06 0Z"
                fill="#8A8A8A"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm0-1.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Z"
                fill="#8A8A8A"
              />
            </svg>
          </button>
        </div>

        <CsIcon v-if="suffixIcon" :name="suffixIcon" />
        <slot v-else name="suffix" />
      </div>
    </div>

    <CsFormError v-if="props.error" class="mt-1">
      {{ props.error }}
    </CsFormError>

    <CsFormHelpText v-if="props.helpText || hasSlot('helpText')" class="mt-1">
      <slot v-if="hasSlot('helpText')" name="helpText" />
      <span v-else>{{ props.helpText }}</span>
    </CsFormHelpText>
  </div>
</template>
